import React, { useState, useEffect, useContext, useRef } from 'react';
import './OrderTracking.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { StoreContext } from '../../Context/StoreContext';
import { io } from 'socket.io-client';
import { assets } from '../../assets/assets';

const OrderTracking = ({ orderId, onClose }) => {
  const { t } = useTranslation();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { url, token } = useContext(StoreContext);
  const socketRef = useRef(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const mapRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const customerMarkerRef = useRef(null);
  const routeRenderer = useRef(null);
  const directionsService = useRef(null);
  
  // Enhanced live tracking states
  const [realTimeETA, setRealTimeETA] = useState(null);
  const [distance, setDistance] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [trafficCondition, setTrafficCondition] = useState('unknown');
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  
  // Function to format date in a friendly way
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Enhanced time remaining calculation with real-time ETA
  const getTimeRemaining = (estimatedDate) => {
    // Use real-time ETA if available and more recent
    if (realTimeETA && realTimeETA.timestamp > Date.now() - 120000) { // 2 minutes freshness
      const etaMinutes = Math.round(realTimeETA.duration / 60);
      if (etaMinutes <= 0) return t('orderTracking.arriving_now');
      if (etaMinutes === 1) return `1 ${t('orderTracking.minute')}`;
      if (etaMinutes < 60) return `${etaMinutes} ${t('orderTracking.minutes')}`;
      
      const hours = Math.floor(etaMinutes / 60);
      const mins = etaMinutes % 60;
      return `${hours}${t('orderTracking.hour')} ${mins}${t('orderTracking.minutes')}`;
    }
    
    // Fallback to original calculation
    if (!estimatedDate) return t('orderTracking.calculating_eta');
    
    const now = new Date();
    const estimated = new Date(estimatedDate);
    const diffMs = estimated - now;
    
    if (diffMs <= 0) return t('orderTracking.expected_shortly');
    
    const diffMins = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    if (diffHours > 0) {
      return `${diffHours} ${diffHours > 1 ? t('orderTracking.hours') : t('orderTracking.hour')} ${remainingMins} ${remainingMins !== 1 ? t('orderTracking.minutes') : t('orderTracking.minute')}`;
    } else {
      return `${remainingMins} ${remainingMins !== 1 ? t('orderTracking.minutes') : t('orderTracking.minute')}`;
    }
  };
  
  // Format distance for display
  const formatDistance = (distanceInMeters) => {
    if (!distanceInMeters) return t('orderTracking.calculating');
    
    if (distanceInMeters < 1000) {
      return `${Math.round(distanceInMeters)} ${t('orderTracking.meters')}`;
    } else {
      const km = (distanceInMeters / 1000).toFixed(1);
      return `${km} ${t('orderTracking.kilometers')}`;
    }
  };
  
  // Get traffic condition display
  const getTrafficDisplay = (condition) => {
    const trafficMap = {
      'light': { text: t('orderTracking.light_traffic'), color: '#28a745', icon: '🟢' },
      'moderate': { text: t('orderTracking.moderate_traffic'), color: '#ffc107', icon: '🟡' },
      'heavy': { text: t('orderTracking.heavy_traffic'), color: '#dc3545', icon: '🔴' },
      'unknown': { text: t('orderTracking.checking_traffic'), color: '#6c757d', icon: '⚪' }
    };
    
    return trafficMap[condition] || trafficMap['unknown'];
  };
  
  // Calculate route and ETA using Google Maps APIs
  const calculateRouteAndETA = async (driverLat, driverLng, customerLat, customerLng) => {
    if (!window.google || !directionsService.current) return;
    
    setIsCalculatingRoute(true);
    
    try {
      const request = {
        origin: new window.google.maps.LatLng(driverLat, driverLng),
        destination: new window.google.maps.LatLng(customerLat, customerLng),
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: window.google.maps.TrafficModel.BEST_GUESS
        },
        unitSystem: window.google.maps.UnitSystem.METRIC
      };
      
      directionsService.current.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const route = result.routes[0];
          const leg = route.legs[0];
          
          // Update route visualization
          if (routeRenderer.current) {
            routeRenderer.current.setDirections(result);
          }
          
          // Extract route information
          const distanceMeters = leg.distance.value;
          const durationSeconds = leg.duration_in_traffic ? leg.duration_in_traffic.value : leg.duration.value;
          
          // Determine traffic condition based on duration difference
          let traffic = 'unknown';
          if (leg.duration_in_traffic && leg.duration) {
            const trafficDelay = leg.duration_in_traffic.value - leg.duration.value;
            const delayPercentage = trafficDelay / leg.duration.value;
            
            if (delayPercentage < 0.1) traffic = 'light';
            else if (delayPercentage < 0.3) traffic = 'moderate';
            else traffic = 'heavy';
          }
          
          // Update states
          setDistance(distanceMeters);
          setRealTimeETA({
            duration: durationSeconds,
            timestamp: Date.now(),
            arrivalTime: new Date(Date.now() + durationSeconds * 1000)
          });
          setRouteInfo({
            distance: leg.distance.text,
            duration: leg.duration_in_traffic ? leg.duration_in_traffic.text : leg.duration.text,
            normalDuration: leg.duration.text
          });
          setTrafficCondition(traffic);
        } else {
          // Use simple distance calculation as fallback
          const directDistance = window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(driverLat, driverLng),
            new window.google.maps.LatLng(customerLat, customerLng)
          );
          setDistance(directDistance);
        }
        
        setIsCalculatingRoute(false);
      });
    } catch (error) {
      setIsCalculatingRoute(false);
    }
  };
  
  // Function to get status percentage for progress bar
  const getStatusPercentage = (status, deliveryType = 'delivery') => {
    if (deliveryType === 'pickup') {
      const pickupStatusMap = {
        'Created': 20,
        'Paid': 20,
        'Restaurant Confirmed': 40,
        'Food Processing': 60,
        'Ready for Pickup': 80,
        'Picked Up': 100,
        'Cancelled': 0
      };
      return pickupStatusMap[status] || 0;
    }
    
    const deliveryStatusMap = {
      'Created': 12.5,
      'Restaurant Confirmed': 25,
      'Driver on the way to Restaurant': 37.5,
      'Food Processing': 50,
      'Ready': 62.5,
      'Order picked up by driver': 75,
      'Out for Delivery': 87.5,
      'Driver coming with your order': 87.5,
      'Delivered': 100,
      'Completed': 100,
      'Cancelled': 0
    };
    return deliveryStatusMap[status] || 0;
  };

  // Function to map backend status to customer-friendly display
  const getDisplayStatus = (backendStatus, deliveryType = 'delivery') => {
    if (deliveryType === 'pickup') {
      const pickupStatusMap = {
        'Created': 'Order Placed',
        'Paid': 'Order Placed',
        'Restaurant Confirmed': 'Restaurant Confirmed',
        'Food Processing': 'Food Processing',
        'Ready for Pickup': 'Ready for Pickup',
        'Picked Up': 'Picked Up',
        'Cancelled': 'Cancelled'
      };
      return pickupStatusMap[backendStatus] || backendStatus;
    }
    
    // Delivery order statuses
    const deliveryStatusMap = {
      'Created': 'Created',
      'Paid': 'Created',
      'Restaurant Confirmed': 'Restaurant Confirmed',
      'Driver on the way to Restaurant': 'Driver on the way to Restaurant',
      'Food Processing': 'Food Processing',
      'Ready': 'Ready',
      'Order picked up by driver': 'Order picked up by driver',
      'Out for Delivery': 'Driver coming with your order',
      'Delivered': 'Delivered',
      'Completed': 'Delivered',
      'Cancelled': 'Cancelled'
    };
    
    return deliveryStatusMap[backendStatus] || backendStatus;
  };
  
    // Enhanced map initialization - separate effect for better timing
  useEffect(() => {
    if (window.google && driverLocation && tracking && 
        tracking.status === 'Out for Delivery' && tracking.customer?.address?.coordinates) {
      
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        const mapElement = document.getElementById('driver-map');
        if (!mapElement) {
          return;
        }
        
        const driverLat = driverLocation.latitude;
        const driverLng = driverLocation.longitude;
        const customerLat = parseFloat(tracking.customer.address.coordinates.latitude);
        const customerLng = parseFloat(tracking.customer.address.coordinates.longitude);
        
        // Create map if it doesn't exist
        if (!mapRef.current) {
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(new window.google.maps.LatLng(driverLat, driverLng));
          bounds.extend(new window.google.maps.LatLng(customerLat, customerLng));
          
          const mapOptions = {
            center: bounds.getCenter(),
            zoom: 13,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            mapTypeControl: false,
            streetViewControl: false,
            trafficLayer: true
          };
          
          try {
            mapRef.current = new window.google.maps.Map(mapElement, mapOptions);
            
            // Initialize directions service and renderer
            directionsService.current = new window.google.maps.DirectionsService();
            routeRenderer.current = new window.google.maps.DirectionsRenderer({
              suppressMarkers: false,
              polylineOptions: {
                strokeColor: '#4285F4',
                strokeWeight: 5,
                strokeOpacity: 0.8
              }
            });
            routeRenderer.current.setMap(mapRef.current);
            
            // Create driver marker
            driverMarkerRef.current = new window.google.maps.Marker({
              position: { lat: driverLat, lng: driverLng },
              map: mapRef.current,
              title: 'Your Driver',
              icon: {
                url: 'https://maps.google.com/mapfiles/ms/icons/delivery.png',
                scaledSize: new window.google.maps.Size(40, 40)
              },
              zIndex: 1000
            });
            
            // Create customer marker
            customerMarkerRef.current = new window.google.maps.Marker({
              position: { lat: customerLat, lng: customerLng },
              map: mapRef.current,
              title: 'Delivery Address',
              icon: {
                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                scaledSize: new window.google.maps.Size(32, 32)
              }
            });
            
            // Fit map to show both markers
            mapRef.current.fitBounds(bounds);
            
            // Add some padding to the bounds
            const padding = { top: 50, right: 50, bottom: 50, left: 50 };
            mapRef.current.fitBounds(bounds, padding);
            
                      // Calculate route and ETA
          calculateRouteAndETA(driverLat, driverLng, customerLat, customerLng);
        } catch (error) {
          console.error('Error creating map:', error);
        }
      } else {
        // Update driver marker position
        if (driverMarkerRef.current) {
          driverMarkerRef.current.setPosition({ lat: driverLat, lng: driverLng });
        }
          
          // Recalculate route
          calculateRouteAndETA(driverLat, driverLng, customerLat, customerLng);
        }
      }, 500); // Longer delay to ensure DOM is ready
      
      return () => clearTimeout(timeoutId);
    }
  }, [driverLocation, tracking]);
  
  // Load Google Maps API with additional libraries
  useEffect(() => {
    if (tracking && tracking.status === 'Out for Delivery' && !window.google) {
      const initMap = () => {
        // Google Maps API loaded
      };
      
      const script = document.createElement('script');
      // Use Vite environment variable or fallback
      const googleMapsApiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyDXTZc0WhG2qeUXAKMnXpMQ_w1eUBlqTeo';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=geometry&callback=initMap`;
      script.async = true;
      script.defer = true;
      
      window.initMap = initMap;
      document.head.appendChild(script);
      
      return () => {
        document.head.removeChild(script);
        delete window.initMap;
      };
    }
  }, [tracking]);
  
  // Real-time ETA updates every 30 seconds when out for delivery
  useEffect(() => {
    if (tracking && tracking.status === 'Out for Delivery' && 
        driverLocation && tracking.customer?.address?.coordinates) {
      
      const intervalId = setInterval(() => {
        const driverLat = driverLocation.latitude;
        const driverLng = driverLocation.longitude;
        const customerLat = parseFloat(tracking.customer.address.coordinates.latitude);
        const customerLng = parseFloat(tracking.customer.address.coordinates.longitude);
        
        calculateRouteAndETA(driverLat, driverLng, customerLat, customerLng);
      }, 30000); // Update every 30 seconds
      
      return () => clearInterval(intervalId);
    }
  }, [tracking, driverLocation]);
  
  // Initialize Socket.IO connection
  useEffect(() => {
    // Don't connect socket if we don't have a token
    if (!token) {
      console.log('No authentication token available for socket connection');
      return;
    }
    
    // Clear any existing connection
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    
    // Create new socket connection
    const socketUrl = url.replace('/api', '');
    
    socketRef.current = io(socketUrl, {
      // Add authentication if needed
      auth: {
        token: token
      },
      // Disable credentials to match server CORS settings
      withCredentials: false,
      // Add timeout and retry settings
      timeout: 10000,
      retries: 3
    });
    
    // Set up event listeners
    socketRef.current.on('connect', () => {
      socketRef.current.emit('join_order', orderId);
    });
    
    socketRef.current.on('connect_error', (error) => {
      // Socket connection failed - will fall back to manual refresh
    });
    
    socketRef.current.on('disconnect', (reason) => {
      // Socket disconnected - will fall back to manual refresh
    });
    
    // Handle order status updates
    socketRef.current.on('order_status_updated', (data) => {
      if (data.orderId === orderId) {
        setTracking(prevTracking => {
          if (!prevTracking) return null;
          
          // Create a deep copy of the tracking object to avoid mutation
          const updatedTracking = { ...prevTracking };
          
          // Update the status and estimated delivery time
          updatedTracking.status = data.status;
          updatedTracking.estimatedDelivery = data.estimatedDelivery;
          
          // Add the new status update to history if it's not already there
          if (data.statusUpdate && updatedTracking.statusHistory) {
            // Check if this status already exists in the history
            const exists = updatedTracking.statusHistory && 
                          Array.isArray(updatedTracking.statusHistory) && 
                          updatedTracking.statusHistory.some(
                            item => item.timestamp === data.statusUpdate.timestamp
                          );
            
            if (!exists) {
              updatedTracking.statusHistory = [
                data.statusUpdate,
                ...updatedTracking.statusHistory
              ];
            }
          }
          
          return updatedTracking;
        });
        
        // If status change involves driver assignment, update driver info
        if (data.driverName) {
          setDriverInfo({
            name: data.driverName,
            phone: data.driverPhone,
            vehicleInfo: data.vehicleInfo
          });
        }
        
        // Show notification for the update
        toast.info(`Order status updated to "${data.status}"`);
        setLastUpdate(new Date());
        
        // Play a notification sound
        const audio = new Audio('/notification.mp3');
        audio.play().catch(err => console.log('Audio playback error:', err));
      }
    });
    
    // Handle driver location updates
    socketRef.current.on('driver_location_updated', (data) => {
      if (data.orderId === orderId && data.driverLocation) {
        setDriverLocation({
          latitude: data.driverLocation.latitude,
          longitude: data.driverLocation.longitude,
          updatedAt: data.driverLocation.timestamp
        });
      }
    });
    
    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_order', orderId);
        socketRef.current.disconnect();
      }
    };
  }, [orderId, url, token]);
  
  // Fetch order tracking data
  const fetchOrderTracking = async () => {
    try {
      setLoading(true);
      
      // Check if we have a token
      if (!token) {
        setError('Please log in to view order tracking');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${url}/api/order/tracking/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTracking(response.data.data);
        
        // Set driver info if available
        if (response.data.data.driver) {
          setDriverInfo(response.data.data.driver);
        }
        
        // Set current driver location if available
        if (response.data.data.currentLocation) {
          setDriverLocation(response.data.data.currentLocation);
        } else if (response.data.data.status === 'Out for Delivery') {
          // Enhanced coordinate extraction with better fallback handling
          console.log('OrderTracking - Order is Out for Delivery, checking for coordinates...');
          
          // Try multiple possible coordinate sources with enhanced debugging
          let customerCoords = null;
          let coordinatesSource = 'none';
          
          console.log('OrderTracking - Debugging coordinate extraction:');
          console.log('OrderTracking - data.data.customerCoordinates:', JSON.stringify(response.data.data.customerCoordinates));
          console.log('OrderTracking - data.data.customer?.address:', JSON.stringify(response.data.data.customer?.address));
          console.log('OrderTracking - data.data.customer?.address?.coordinates:', JSON.stringify(response.data.data.customer?.address?.coordinates));
          
          // First try customerCoordinates
          if (response.data.data.customerCoordinates && 
              typeof response.data.data.customerCoordinates === 'object' && 
              response.data.data.customerCoordinates.latitude && 
              response.data.data.customerCoordinates.longitude) {
            customerCoords = response.data.data.customerCoordinates;
            coordinatesSource = 'customerCoordinates';
            console.log('OrderTracking - Found coordinates in customerCoordinates:', customerCoords);
          }
          // Then try customer.address.coordinates with various property names
          else if (response.data.data.customer?.address?.coordinates && 
                   typeof response.data.data.customer.address.coordinates === 'object') {
            const coords = response.data.data.customer.address.coordinates;
            console.log('OrderTracking - Examining customer.address.coordinates object:', coords);
            
            // Try different possible property names
            if (coords.latitude && coords.longitude) {
              customerCoords = { latitude: coords.latitude, longitude: coords.longitude };
              coordinatesSource = 'customer.address.coordinates (lat/lng)';
              console.log('OrderTracking - Found coordinates with latitude/longitude:', customerCoords);
            } else if (coords.lat && coords.lng) {
              customerCoords = { latitude: coords.lat, longitude: coords.lng };
              coordinatesSource = 'customer.address.coordinates (lat/lng)';
              console.log('OrderTracking - Found coordinates with lat/lng:', customerCoords);
            } else if (coords.lat && coords.lon) {
              customerCoords = { latitude: coords.lat, longitude: coords.lon };
              coordinatesSource = 'customer.address.coordinates (lat/lon)';
              console.log('OrderTracking - Found coordinates with lat/lon:', customerCoords);
            } else if (coords.x && coords.y) {
              customerCoords = { latitude: coords.y, longitude: coords.x };
              coordinatesSource = 'customer.address.coordinates (x/y)';
              console.log('OrderTracking - Found coordinates with x/y:', customerCoords);
            } else {
              console.log('OrderTracking - No valid coordinate properties found in object:', Object.keys(coords));
            }
          }
          // Then try customer.address.coordinates array format
          else if (response.data.data.customer?.address?.coordinates && 
                   Array.isArray(response.data.data.customer.address.coordinates) &&
                   response.data.data.customer.address.coordinates.length >= 2) {
            customerCoords = {
              latitude: response.data.data.customer.address.coordinates[1],
              longitude: response.data.data.customer.address.coordinates[0]
            };
            coordinatesSource = 'customer.address.coordinates (array)';
            console.log('OrderTracking - Found coordinates in customer.address.coordinates array:', customerCoords);
          }
          
          if (customerCoords && customerCoords.latitude && customerCoords.longitude) {
            console.log('OrderTracking - Customer coordinates found from source:', coordinatesSource);
            console.log('OrderTracking - Customer coordinates:', customerCoords);
            
            // Create a test driver location about 2km away from customer
            const testDriverLocation = {
              latitude: customerCoords.latitude - 0.015, // ~1.7km south
              longitude: customerCoords.longitude + 0.008, // ~0.9km east
              timestamp: new Date()
            };
            
            console.log('OrderTracking - Setting test driver location:', testDriverLocation);
            setDriverLocation(testDriverLocation);
          } else {
            console.log('OrderTracking - No customer coordinates found for live tracking');
            console.log('OrderTracking - Available coordinate data:', {
              customerCoordinates: response.data.data.customerCoordinates,
              customerAddress: response.data.data.customer?.address,
              customerAddressCoordinates: response.data.data.customer?.address?.coordinates
            });
            
            // Use default Bujumbura coordinates for demo purposes
            const defaultCustomerCoords = {
              latitude: -3.3731, // Bujumbura coordinates
              longitude: 29.9189
            };
            
            console.log('OrderTracking - Using default coordinates for demo:', defaultCustomerCoords);
            
            // Create a test driver location about 2km away from default location
            const testDriverLocation = {
              latitude: defaultCustomerCoords.latitude - 0.015, // ~1.7km south
              longitude: defaultCustomerCoords.longitude + 0.008, // ~0.9km east
              timestamp: new Date()
            };
            
            console.log('OrderTracking - Setting test driver location with default coordinates:', testDriverLocation);
            setDriverLocation(testDriverLocation);
          }
        }
        
        setError(null);
        setLastUpdate(new Date());
      } else {
        setError('Failed to fetch tracking info');
        toast.error("Error: " + response.data.message);
      }
    } catch (error) {
      console.error("Error fetching tracking:", error);
      
      if (error.response?.status === 403) {
        setError('You do not have permission to view this order. Please make sure you are logged in with the correct account.');
        toast.error("Access denied: You can only view your own orders");
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please log in again.');
        toast.error("Please log in to view order tracking");
      } else if (error.response?.status === 404) {
        setError('Order not found. Please check the order ID.');
        toast.error("Order not found");
      } else {
        setError('Failed to connect to server');
        toast.error("Failed to load tracking information");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Set up auto-refresh every 30 seconds (as backup if socket fails)
  useEffect(() => {
    fetchOrderTracking();
    
    // Only set up auto-refresh for active orders
    if (tracking && tracking.status !== 'Delivered' && tracking.status !== 'Cancelled') {
      const intervalId = setInterval(() => {
        fetchOrderTracking();
      }, 60000); // 60 seconds as backup
      
      return () => clearInterval(intervalId);
    }
  }, [orderId, token, tracking?.status]);
  
  if (loading && !tracking) {
    return (
      <div className="order-tracking">
        <div className="tracking-header">
          <h3>Order Tracking</h3>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="tracking-loading">
          <p>Loading tracking information...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="order-tracking">
        <div className="tracking-header">
          <h3>Order Tracking</h3>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="tracking-error">
          <p>{error}</p>
          <button onClick={fetchOrderTracking} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }
  
  if (!tracking) return null;
  
  return (
    <div className="order-tracking">
      <div className="tracking-header">
        <h3>{t('orderTracking.order_number')}{orderId.slice(-6)}</h3>
        <button onClick={onClose} className="close-button">×</button>
      </div>
      
      <div className="tracking-content">
        {/* Current status section moved to top */}
        <div className="tracking-status">
          <h4>{t('orderTracking.current_status')}: <span className={getDisplayStatus(tracking.status, tracking.deliveryType).toLowerCase().replace(/\s+/g, '-')}>{getDisplayStatus(tracking.status, tracking.deliveryType)}</span></h4>
          {tracking.estimatedDelivery && tracking.status !== 'Delivered' && tracking.status !== 'Cancelled' && (
            <div className="estimated-delivery">
              <p>{t('orderTracking.estimated_delivery')}: <strong>{formatDate(tracking.estimatedDelivery)}</strong></p>
              <p>Time remaining: <strong>{getTimeRemaining(tracking.estimatedDelivery)}</strong></p>
            </div>
          )}
        </div>

        {/* Driver info section - Only show for delivery orders */}
        {tracking.deliveryType !== 'pickup' && 
         (tracking.status === 'Driver on the way to Restaurant' || 
          tracking.status === 'Food Processing' || 
          tracking.status === 'Ready' || 
          tracking.status === 'Out for Delivery') && (tracking.driver || driverInfo) && (
          <div className="driver-info-section">
            <h4>🚗 {t('orderTracking.your_delivery_driver')}</h4>
            {(() => {
              // Check multiple sources for driver information
              const driverOnWayStatus = tracking.statusHistory?.find(
                status => status.status === 'Driver on the way to Restaurant' && status.driverInfo
              );
              const outForDeliveryStatus = tracking.statusHistory?.find(
                status => status.status === 'Out for Delivery' && status.driverInfo
              );
              
              // Get driver info from various sources
              const currentDriverInfo = driverOnWayStatus?.driverInfo || 
                                       outForDeliveryStatus?.driverInfo ||
                                       driverInfo || 
                                       tracking.driver;
              
              if (currentDriverInfo) {
                return (
                  <div className="driver-card">
                    <div className="driver-header">
                      <div className="driver-avatar">
                        <span className="driver-emoji">👤</span>
                      </div>
                      <div className="driver-basic-info">
                        <h5 className="driver-name">
                          {currentDriverInfo.name || t('orderTracking.your_driver')}
                        </h5>
                        <div className="driver-status-badge">
                          <span className="status-indicator">
                            {tracking.status === 'Driver on the way to Restaurant' ? '🏃‍♂️' : 
                             tracking.status === 'Food Processing' ? '👨‍🍳' : 
                             tracking.status === 'Ready' ? '✅' : '🚚'}
                          </span>
                          <span>
                            {tracking.status === 'Driver on the way to Restaurant' ? 'Going to restaurant' : 
                             tracking.status === 'Food Processing' ? 'Food being prepared' : 
                             tracking.status === 'Ready' ? 'Ready for pickup' : 'On the way to you'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="driver-contact-section">
                      {currentDriverInfo.phone && (
                        <div className="contact-item">
                          <span className="contact-label">📞 {t('myOrders.phone')}:</span>
                          <a href={`tel:${currentDriverInfo.phone}`} className="driver-phone-link">
                            {currentDriverInfo.phone}
                          </a>
                        </div>
                      )}
                      
                      {(currentDriverInfo.vehicle || currentDriverInfo.vehicleInfo) && (
                        <div className="contact-item">
                          <span className="contact-label">🚗 {t('orderTracking.vehicle')}:</span>
                          <span className="vehicle-info">
                            {currentDriverInfo.vehicle || currentDriverInfo.vehicleInfo}
                          </span>
                        </div>
                      )}
                      
                      {currentDriverInfo.plateNumber && (
                        <div className="contact-item">
                          <span className="contact-label">🔢 Plate:</span>
                          <span className="plate-number">{currentDriverInfo.plateNumber}</span>
                        </div>
                      )}
                    </div>
                    
                    {currentDriverInfo.phone && (
                      <div className="driver-actions">
                        <a href={`tel:${currentDriverInfo.phone}`} className="call-driver-btn">
                          <span className="call-icon">📞</span>
                          {t('orderTracking.call_driver')}
                        </a>
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div className="driver-card">
                    <div className="driver-pending-info">
                      <span className="pending-icon">⏳</span>
                      <p>{t('orderTracking.driver_not_assigned')}</p>
                    </div>
                  </div>
                );
              }
            })()}
            
            {/* Enhanced live tracking with real-time ETA and route - only for delivery orders */}
            {tracking.deliveryType !== 'pickup' && tracking.status === 'Out for Delivery' && (
              <div className="driver-location-section">
                <div className="live-tracking-header">
                  <h4>📍 {t('orderTracking.live_tracking')}</h4>
                  {isCalculatingRoute && (
                    <div className="calculating-indicator">
                      <span className="calculating-spinner">⏳</span>
                      <span>{t('orderTracking.updating_route')}</span>
                    </div>
                  )}
                </div>
                
                {/* Real-time tracking stats */}
                <div className="tracking-stats">
                  <div className="stat-card eta-card">
                    <div className="stat-icon">⏱️</div>
                    <div className="stat-content">
                      <div className="stat-label">{t('orderTracking.estimated_arrival')}</div>
                      <div className="stat-value">
                        {realTimeETA ? getTimeRemaining() : t('orderTracking.calculating')}
                      </div>
                      {realTimeETA && (
                        <div className="stat-subtext">
                          ETA: {realTimeETA.arrivalTime.toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="stat-card distance-card">
                    <div className="stat-icon">📏</div>
                    <div className="stat-content">
                      <div className="stat-label">{t('orderTracking.distance')}</div>
                      <div className="stat-value">{formatDistance(distance)}</div>
                      {routeInfo && (
                        <div className="stat-subtext">{t('orderTracking.via_fastest_route')}</div>
                      )}
                    </div>
                  </div>
                  
                  <div className="stat-card traffic-card">
                    <div className="stat-icon">{getTrafficDisplay(trafficCondition).icon}</div>
                    <div className="stat-content">
                      <div className="stat-label">{t('orderTracking.traffic')}</div>
                      <div 
                        className="stat-value" 
                        style={{ color: getTrafficDisplay(trafficCondition).color }}
                      >
                        {getTrafficDisplay(trafficCondition).text}
                      </div>
                      {routeInfo && routeInfo.duration !== routeInfo.normalDuration && (
                        <div className="stat-subtext">
                          +{Math.round((parseInt(routeInfo.duration) - parseInt(routeInfo.normalDuration)) / 60)}{t('orderTracking.min_delay')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Map container */}
                <div 
                  id="driver-map" 
                  className="map-container enhanced-map"
                  style={{ height: '280px', width: '100%', borderRadius: '8px', border: '1px solid #ddd' }}
                >
                  {!window.google && (
                    <div className="map-placeholder">
                      <div className="map-loading">
                        <span className="loading-icon">🗺️</span>
                        <p>{t('orderTracking.loading_enhanced_map')}</p>
                        <small>{t('orderTracking.live_tracking_with_route')}</small>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Map legend */}
                {window.google && driverLocation && (
                  <div className="map-legend">
                    <div className="legend-item">
                      <div className="legend-marker driver-marker">🚚</div>
                      <span>{t('orderTracking.your_driver')}</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-marker customer-marker">📍</div>
                      <span>{t('orderTracking.delivery_location')}</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-line"></div>
                      <span>{t('orderTracking.optimal_route')}</span>
                    </div>
                  </div>
                )}
                
                {/* Location update info */}
                {driverLocation && (
                  <div className="location-updated enhanced">
                    <div className="update-info">
                      <span className="location-icon">📍</span>
                      <span>{t('orderTracking.last_location_update')}: {formatDate(driverLocation.updatedAt)}</span>
                    </div>
                    <div className="auto-update-notice">
                      <span className="update-icon">🔄</span>
                      <span>{t('orderTracking.updates_every_30_seconds')}</span>
                    </div>
                  </div>
                )}
                
                {/* Route summary */}
                {routeInfo && (
                  <div className="route-summary">
                    <div className="summary-title">{t('orderTracking.route_summary')}</div>
                    <div className="summary-details">
                      <span>{t('orderTracking.distance')}: {routeInfo.distance}</span>
                      <span>{t('orderTracking.duration')}: {routeInfo.duration}</span>
                      {trafficCondition !== 'light' && (
                        <>
                        
                          <span style={{ color: getTrafficDisplay(trafficCondition).color }}>
                            {getTrafficDisplay(trafficCondition).text}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Pickup-specific tracking information */}
            {tracking.deliveryType === 'pickup' && (
              <div className="pickup-tracking-section">
                <div className="pickup-header">
                  <h4>🏪 {t('orderTracking.pickup_information')}</h4>
                </div>
                
                <div className="pickup-details">
                  <div className="pickup-location">
                    <div className="pickup-icon">📍</div>
                    <div className="pickup-content">
                      <div className="pickup-label">{t('orderTracking.pickup_location')}</div>
                      <div className="pickup-value">{t('myOrders.restaurant')}</div>
                      {tracking.restaurant && (
                        <div className="restaurant-info">
                          <strong>{tracking.restaurant.name}</strong>
                          {tracking.restaurant.address && (
                            <div className="restaurant-address">
                              {typeof tracking.restaurant.address === 'object' 
                                ? [
                                    tracking.restaurant.address.street,
                                    tracking.restaurant.address.quartier,
                                    tracking.restaurant.address.city
                                  ].filter(Boolean).join(', ')
                                : tracking.restaurant.address
                              }
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="pickup-instructions">
                    <div className="pickup-icon">📋</div>
                    <div className="pickup-content">
                      <div className="pickup-label">{t('orderTracking.pickup_instructions')}</div>
                      <div className="pickup-value">
                        <ul>
                          <li>{t('orderTracking.bring_valid_id')}</li>
                          <li>{t('orderTracking.show_order_confirmation')}</li>
                          <li>{t('orderTracking.pay_at_pickup')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {tracking.status === 'Ready for Pickup' && (
                    <div className="pickup-ready-notice">
                      <div className="ready-icon">✅</div>
                      <div className="ready-content">
                        <div className="ready-label">{t('orderTracking.order_ready')}</div>
                        <div className="ready-message">{t('orderTracking.come_to_restaurant')}</div>
                      </div>
                    </div>
                  )}
                  
                  {tracking.status === 'Picked Up' && (
                    <div className="pickup-completed-notice">
                      <div className="completed-icon">🎉</div>
                      <div className="completed-content">
                        <div className="completed-label">{t('orderTracking.order_picked_up')}</div>
                        <div className="completed-message">{t('orderTracking.thank_you_service')}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="tracking-timeline">
          <h4>{t('orderTracking.status_updates')}</h4>
          <div className="timeline">
            {tracking.statusHistory && tracking.statusHistory.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-point"></div>
                <div className="timeline-content">
                  <div className="timeline-date">{formatDate(item.timestamp)}</div>
                  <div className="timeline-status">
                    <span className={item.status.toLowerCase().replace(/\s+/g, '-')}>
                      {item.status}
                    </span>
                  </div>
                  {item.note && <div className="timeline-note">{item.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Show delivery photo if available */}
        {tracking.status === 'Delivered' && tracking.deliveryPhoto && (
          <div className="delivery-confirmation">
            <h4>{t('orderTracking.delivery_confirmation')}</h4>
            <div className="delivery-photo">
              <img src={tracking.deliveryPhoto} alt="Delivery Confirmation" />
            </div>
          </div>
        )}
        
        <div className="tracking-refresh">
          <button onClick={fetchOrderTracking} className="refresh-button">
            {t('orderTracking.refresh_tracking')}
          </button>
          <div className="auto-refresh-notice">
            {tracking.status !== 'Delivered' && tracking.status !== 'Cancelled' ? (
              <div>
                <div className="connection-status">
                  <span className={socketRef.current && socketRef.current.connected ? 'connected' : 'disconnected'}>●</span>
                  {socketRef.current && socketRef.current.connected 
                    ? t('orderTracking.real_time_updates_active')
                    : t('orderTracking.real_time_updates_disconnected')}
                </div>
                {lastUpdate && (
                  <div className="last-update">
                    {t('orderTracking.last_updated')}: {new Date(lastUpdate).toLocaleTimeString()}
                  </div>
                )}
              </div>
            ) : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking; 